//  ==========================================================================
//
//  What We Do
//
//  ==========================================================================
import React, { PureComponent } from 'react';

import { graphql } from 'gatsby';

import {
	BlockquoteBreaker,
	CardFeature,
	CardFeatureGroup,
	GridStandard,
	HeroStandard,
	MetaTags,
	Section,
} from '../components';

import { allRoutes, normalizeCuratedContent } from '../helpers';

//  ==========================================================================
//  Component
//  ==========================================================================
export default class WhatWeDo extends PureComponent {
	constructor( props ) {
		super( props );

		this._whatWeDoData = {
			description:
				props.data?.allWordpressWpStaticPageMeta?.edges?.[0]?.node
					?.what_we_do_meta_desc,
			hero: props.data?.heroImage?.childImageSharp?.fluid,
			heroAlt: 'Close-up of blue building windows reflecting the sky',
			jumpLinks: [
				{
					name: 'Services',
					path: '#services',
				},
			],
			project_types: this.props.data.project_types.nodes.map(
				normalizeCuratedContent
			),
			services: this.props.data.services.nodes.map( normalizeCuratedContent ),
			blockquoteImage: props.data?.blockquoteBreakerImage?.childImageSharp?.fluid,
			jobsImage: props.data?.jobsImage?.childImageSharp?.fluid,
			fellowshipsImage: props.data?.fellowshipsImage?.childImageSharp?.fluid,
		};
	}

	render() {
		const {
			description,
			hero,
			heroAlt,
			jumpLinks,
			project_types,
			services,
			blockquoteImage,
			jobsImage,
			fellowshipsImage,
		} = this._whatWeDoData;

		return (
			<main id="main">
				<MetaTags post_title="What We Do" description={description} />

				{!!hero && (
					<HeroStandard
						hero={hero}
						heroAlt={heroAlt}
						label="What We Do"
						post_title="We deliver designs that inspire, connect and perform."
						description="Whether we are creating memorable experiences for sports fans; providing a world away to resort guests; or enabling teens living with cancer to support one another — we believe design can change people’s lives for the better."
						jumpLinks={jumpLinks}
					/>
				)}

				<div className="content-contain">
					<Section post_title="Project Types" titleVisible={true}>
						<GridStandard content={project_types} />
					</Section>

					<Section post_title="Services" titleVisible={true}>
						<GridStandard
							content={services}
							cardStyle="no-image"
							gridItemTracking="js-gtm-service-card"
						/>
					</Section>
				</div>

				<BlockquoteBreaker
					image={blockquoteImage}
					imageAlt="Headshot of Upali Nanda"
					quote="Our profession has seen a paradigm shift in how we talk about research. We no longer talk about research as what we do, but rather, as who we are. We elevate design through research."
					name="Upali Nanda"
					post_title={'Director of Research \nPrincipal \nPhD, Assoc. AIA, EDAC, ACHE'
						.split( '\n' )
						.map( ( item, i ) => (
							<p key={i}>{item}</p>
						) )}
					linkedin="upali-nanda-aa696a4"
					path="/people/upali-nanda"
				/>

				<div className="content-group content-contain">
					<CardFeatureGroup>
						<CardFeature
							image={jobsImage}
							imageAlt="Five people sitting around a table laughing and looking at a magazine"
							label="Careers"
							post_title="What's It Like to Work With Us?"
							cta="Join Our Team"
							link={allRoutes.join}
						/>
						<CardFeature
							image={fellowshipsImage}
							imageAlt="People looking at models laid out on a table in front of them"
							label="People"
							post_title="Meet Our Team"
							cta="Learn More About Our People"
							link={allRoutes.join_fellowships}
						/>
					</CardFeatureGroup>
				</div>
			</main>
		);
	}
}

export const whatWeDoQuery = graphql`
	query WhatWeDoQuery {
		project_types: allWordsbyCollections(
			sort: { fields: [post_title], order: ASC }
			filter: {
				post_type: { eq: "project_type" }
			}
		) {
			nodes {
				...PostFields
				...HeroComponent

				...ThumbnailFields
				business_contact {
					relationshipByID {
						...PersonFragment
					}
				}
				short_summary
				description
				audience_needs {
					audience_need
					curated_content {
						relationshipByID {
							...CardComponent
						}
					}
				}
			}
		}

		services: allWordsbyCollections(
			sort: { fields: [post_title], order: ASC }
			filter: {
				post_type: { eq: "service" }
			}
		) {
			nodes {
				...PostFields
				...CardComponent
			}
		}

		allWordpressWpStaticPageMeta: allWordsbyCollections(
			filter: { post_type: { eq: "static_page_meta" } }
		) {
			edges {
				node {
					what_we_do_meta_desc
				}
			}
		}
		heroImage: file(relativePath: { regex: "/what-we-do--1-hero.jpg/" }) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		blockquoteBreakerImage: file(
			relativePath: { regex: "/who-we-are--3-quote.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		jobsImage: file(relativePath: { regex: "/shared-card-feature--jobs.jpg/" }) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		fellowshipsImage: file(
			relativePath: { regex: "/shared-card-feature--fellowships-2.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
	}
`;
